<template>
  <b-container class="mt-3">

    <b-row class="mb-3">
      <b-col cols="4">
        <h1 class="mb-4">ToDo App</h1>
        <p>This sample ToDo App demonstrates how you can use an Access Token generated via the OpenID Connect
          Implicit or Auth Code flows to authorize access against a custom API.</p>
        <p>For the purpose of this example the sample API and data will all be held client side in the browser.
          In reality you would have a server side API or API Gateway to protect your resources.</p>

        <h3 class="mb-4">Audience</h3>
        <p>This sample app requires the audience claim in your Access Token be set to: <b>https://example.com/todo</b></p>

        <h3 class="mb-4">Scopes</h3>
        <p>This sample app has 2 scopes. Your Access Token will require the correct scope in order to use the app.</p>
        <p><b>createTodo</b> - Create a new item</p>
        <p><b>deleteTodo</b> - Delete an item</p>

      </b-col>
      <b-col cols="8">
        <b-alert variant="danger" :show="!accessToken">
          <p>
            To try this sample you need to generate an Access Token via the Inspector tab.
          </p>
        </b-alert>
        <b-alert variant="danger" :show="!validAccessToken">
          <p>
            The Access Token is not valid. Generate a new one on the inspector tab and try again.
          </p>
          <p v-if="tokenExpired">
            * Token expired
          </p>
          <p v-if="!audienceValid">
            * Invalid Audience. Must include https://example.com/todo
          </p>
        </b-alert>
        <b-alert variant="info" :show="validAccessToken">
          <p>
            The Access Token signature, expiry, issuer, and audience are valid
          </p>
          <p>
            Scope of access:
            <ul>
              <li>Create Todo: <b>{{hasCreateScope}}</b> <span v-if="!hasCreateScope">- Authenticate again including deleteTodo scope</span></li>
              <li>Delete Todo: <b>{{hasDeleteScope}}</b> <span v-if="!hasDeleteScope">- Authenticate again including deleteTodo scope</span></li>
            </ul>
          </p>
        </b-alert>
        <b-input-group class="mt-3 mb-3">
          <b-form-input id="state"
                            type="text"
                            v-model="itemText"
                            placeholder="Enter something to do."></b-form-input>
          <b-input-group-append>
            <b-button variant="primary" v-on:click="createTodoItem" :disabled="!hasCreateScope">Create ToDo Item</b-button>
          </b-input-group-append>
        </b-input-group>
        <b-list-group>
          <b-list-group-item v-for="todoItem in todoItems" v-bind:key="todoItem.ID">
            {{todoItem.itemText}}
            <b-button type="button" class="float-right"  variant="danger" v-on:click="deleteToDoItem(todoItem.ID)" :disabled="!hasDeleteScope">Remove</b-button>
          </b-list-group-item>
        </b-list-group>
      </b-col>
    </b-row>

  </b-container>
</template>

<script>
export default {
  data () {
    return {
      todoItems: [],
      itemText: '',
      accessToken: null
    }
  },
  mounted() {
    if (localStorage.todoItems) {
      this.todoItems = JSON.parse(localStorage.todoItems);
    }

    if (localStorage["ol_accessToken"]) {
      this.accessToken = localStorage["ol_accessToken"];
    }

    this.reset()
  },
  computed: {
    jwtAccessToken: function () {
      console.log('Access Token:', this.accessToken)
      if (!this.accessToken || this.accessToken && this.accessToken.split('.').length != 3) return null
      var jwt = this.decodeJWT(this.accessToken)
      console.log('JWT Access Token:', jwt)
      return jwt
    },

    validAccessToken: function() {
      console.log('Verify Access Token ', this.accessToken)
      if (!this.accessToken) return false;
      console.log('Access Token Valid: ', this.verifyJWT(this.accessToken))
      return this.verifyJWT(this.accessToken) && this.audienceValid;
    },

    tokenExpired: function() {
      return this.jwtAccessToken && this.jwtAccessToken.exp < Math.floor(Date.now() / 1000)
    },

    audienceValid: function() {
      return this.jwtAccessToken && this.jwtAccessToken.aud.includes('https://example.com/todo')
    },

    hasCreateScope: function() {
      return this.hasScope('createTodo')
    },

    hasDeleteScope: function() {
      return this.hasScope('deleteTodo')
    }
  },
  methods: {
    reset: function () {
      this.itemText = '';
    },

    createTodoItem: function(event) {
      this.todoItems.push({
        ID: Date.now(),
        itemText: this.$sanitize(this.itemText)
      });

      localStorage.todoItems = JSON.stringify(this.todoItems);

      this.reset()
    },

    deleteToDoItem: function(todoItemID) {

      for(let i=0; i<this.todoItems.length; i++){
        let todoItem = this.todoItems[i];
        if(todoItem.ID == todoItemID){
          this.todoItems.splice(this.todoItems.indexOf(todoItem), 1)
        }
      }

      localStorage.todoItems = JSON.stringify(this.todoItems);
    },

    decodeJWT: function (jwt) {
      var parts = jwt.split('.');

      var payload = parts[1]
      payload = payload.replace('-', '+').replace('_', '/');

      return JSON.parse(this.base64UrlDecode(payload))
    },

    base64UrlDecode: function (val) {
      var base64 = val.replace('-', '+').replace('_', '/')
      return window.atob(base64)
    },

    hasScope: function(scope) {
      if (!this.validAccessToken || !this.jwtAccessToken) return false

      var scopes = this.jwtAccessToken.scope.split(' ')
      return scopes.includes(scope)
    },

    verifyJWT: function(jwt) {
      var pem = "-----BEGIN PUBLIC KEY-----\nMIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAz8fZszkUNh1y1iSI6ZCk\nrwoZx1ZcFuQEngI8G/9VPjJXupqbgXedsV0YqDzQzYmdXd/lLb/OYWdyAP1FV6d2\nd4PfVjw4rGLqgYN5hEPFYqDEusiKtXyeh38xl37Nb8LGTX1qdstZjcXRo2YQ64W4\nUyuMko/TGOCxRNJg1fAfxRt1yV/ZeFV/93BMNjubV2D7kvpzaStJmYJi8A6QHqaq\nHaQkxAvYhJVi9XDajD3vvUlTVyOjURAnuaByA749glGBio5N9AfFTnYbHbeBOK3V\nJi6EJZzsuj3+5P4GUTYnSfrScs/kblaoeqt4GkExJqMZXGJTfGnX2UbYAjGHSTAo\nQwIDAQAB\n-----END PUBLIC KEY-----";
      var pubkey = KEYUTIL.getKey(pem);
      return KJUR.jws.JWS.verifyJWT(this.accessToken, pubkey, {
        alg: ['RS256']
      });
    }
  }
}
</script>

<style>
</style>
